// src/views/LandingPage.js
// VERIFIER: MIGRATION-SECTION-v1 — added during 30-day-trial migration session
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, ChevronDown, Loader2, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import FeaturesGrid from './components/FeaturesGrid';
import dashboardPreview from '../assets/dashboard-preview.png';
import appStoreBadge from '../assets/app-store-badge.svg';
import googlePlayBadge from '../assets/google-play-badge.svg';

const LandingPage = () => {
  const APP_SIGNUP_URL = "https://app.spatchy.co/pricing";

  // App store links (homepage placement — campaign tagged)
  const IOS_APP_URL = "https://apps.apple.com/ca/app/spatchy-field-service/id6766078373?ct=website_home";
  const ANDROID_APP_URL = "https://play.google.com/store/apps/details?id=co.spatchy.app&hl=en&referrer=utm_source%3Dspatchy.co%26utm_medium%3Dhome";

  // ---------------------------------------------------------------------------
  // MIGRATION FORM STATE
  // Uses Web3Forms (same as SupportPage.js) — reads REACT_APP_WEB3FORMS_KEY
  // Honeypot 'botcheck' field stays hidden; if a bot fills it, we silently
  // pretend success without actually sending.
  // ---------------------------------------------------------------------------
  const [migrationFormData, setMigrationFormData] = useState({
    name: '',
    email: '',
    phone: '',
    migratingFrom: '',
    botcheck: '',
  });
  const [migrationStatus, setMigrationStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [migrationErrorMessage, setMigrationErrorMessage] = useState('');

  const handleMigrationChange = (e) => {
    setMigrationFormData({ ...migrationFormData, [e.target.name]: e.target.value });
  };

  const handleMigrationSubmit = async (e) => {
    e.preventDefault();

    // Honeypot — silently "succeed" without sending if filled (it's a bot)
    if (migrationFormData.botcheck) {
      setMigrationStatus('success');
      return;
    }

    setMigrationStatus('submitting');
    setMigrationErrorMessage('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          name: migrationFormData.name,
          email: migrationFormData.email,
          subject: `Migration call request from spatchy.co — from ${migrationFormData.migratingFrom}`,
          message:
            `New migration request from spatchy.co\n\n` +
            `Name: ${migrationFormData.name}\n` +
            `Email: ${migrationFormData.email}\n` +
            `Phone: ${migrationFormData.phone}\n` +
            `Migrating from: ${migrationFormData.migratingFrom}`,
          from_name: 'Spatchy Migration Form',
          replyto: migrationFormData.email,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setMigrationStatus('success');
        setMigrationFormData({ name: '', email: '', phone: '', migratingFrom: '', botcheck: '' });
      } else {
        setMigrationStatus('error');
        setMigrationErrorMessage(
          result.message || 'Something went wrong. Please email us directly at support@spatchy.co.'
        );
      }
    } catch (err) {
      setMigrationStatus('error');
      setMigrationErrorMessage(
        'Could not send your message. Please email us directly at support@spatchy.co.'
      );
    }
  };

  return (
    <div className="relative overflow-hidden bg-slate-900 min-h-screen text-white">
      {/* --- SEO & METADATA --- */}
      <Helmet>
        <title>Spatchy | Field Service Software Without the Gatekeeping</title>
        <meta name="description" content="Field service management software for Canadian and US service businesses. Quotes, jobs, routing, invoicing, and team management — every feature included on every plan. No gatekeeping." />
      </Helmet>
      
      {/* Background Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-blue-600/20 rounded-full blur-[120px] -z-10" />
      
      {/* --- HERO SECTION --- */}
      <section className="pt-20 pb-16 px-6 text-center max-w-5xl mx-auto">
        
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-blue-300 text-sm font-medium mb-8">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
          </span>
          Field Service Software — No Gatekeeping. No Per-Seat Fees.
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight">
          Built for the business owner <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
            who does the work and runs the business.
          </span>
        </h1>

        {/* Subhead */}
        <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
          Field service management for crews that go to job sites. Quotes, scheduling, routing, invoicing, and payroll — every feature included on every plan. No per-seat fees, ever.
        </p>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a 
            href={APP_SIGNUP_URL} 
            className="w-full sm:w-auto px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] transform hover:-translate-y-0.5"
          >
            Start 30-Day Free Trial <ArrowRight size={18} />
          </a>
          <Link 
            to="/features"
            className="w-full sm:w-auto px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold rounded-lg transition-all"
          >
            View Features
          </Link>
        </div>

        {/* Feature Ticks */}
        <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-slate-400">
          <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-blue-500"/> Every feature included. No upgrade tiers.</div>
          <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-blue-500"/> No per-seat fees. 5 people or 15 — you pay one price for your tier.</div>
          <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-blue-500"/> 30-day free trial. No credit card required.</div>
        </div>

      </section>

      {/* --- APP PREVIEW --- */}
      <section className="px-6 pb-20 relative z-10">
        <div className="max-w-6xl mx-auto bg-slate-800/50 rounded-xl border border-white/10 p-2 shadow-2xl backdrop-blur-sm">
            <div className="bg-slate-900 rounded-lg overflow-hidden relative group cursor-pointer border border-white/5">
                <img 
                    src={dashboardPreview} 
                    alt="Spatchy Dashboard Interface" 
                    className="w-full h-auto object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-40 group-hover:opacity-0 transition-opacity duration-500"></div>
            </div>
        </div>
        
        {/* Scroll Hint */}
        <div className="flex justify-center mt-12 opacity-50 animate-bounce">
            <ChevronDown className="text-white" size={32} />
        </div>
      </section>

      {/* --- BEFORE / AFTER --- */}
      <section className="py-24 px-6 bg-slate-900">
        <div className="max-w-5xl mx-auto">

          {/* Header */}
          <div className="text-center mb-16">
            <p className="text-blue-400 text-sm font-bold uppercase tracking-widest mb-4">Sound familiar?</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white">
              Your Monday morning, <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">before and after.</span>
            </h2>
          </div>

          {/* Two columns */}
          <div className="grid md:grid-cols-2 gap-6">

            {/* BEFORE */}
            <div className="bg-slate-800/60 border border-red-500/20 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                  <span className="text-red-400 text-sm font-bold">✕</span>
                </div>
                <span className="text-red-400 font-bold uppercase tracking-wider text-sm">Before Spatchy</span>
              </div>
              <ul className="space-y-5">
                {[
                  "Figuring out who goes where — from memory, a whiteboard, or a group text.",
                  "Calling your crew to ask if the job is done.",
                  "Invoicing at 9pm because you forgot during the day.",
                  "A customer asks about their estimate. You have no idea where it went.",
                  "Chasing a payment that's been 'in the mail' for six weeks.",
                  "Sunday night spent building next week's schedule."
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-400">
                    <span className="text-red-500/60 mt-0.5 flex-shrink-0">—</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* AFTER */}
            <div className="bg-slate-800/60 border border-blue-500/20 rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <span className="text-blue-400 text-sm font-bold">✓</span>
                </div>
                <span className="text-blue-400 font-bold uppercase tracking-wider text-sm">With Spatchy</span>
              </div>
              <ul className="space-y-5">
                {[
                  "Routes optimized and loaded into Google Maps before your crew leaves the yard.",
                  "Job status updates the moment your tech marks it done — from their phone.",
                  "Invoice sent before you leave the driveway. Stripe handles the rest.",
                  "Every quote, approval, and message logged against the customer automatically.",
                  "Automated overdue reminders go out. You get paid without following up.",
                  "Weekly Planner builds next week's schedule in minutes, not hours."
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300">
                    <span className="text-blue-400 mt-0.5 flex-shrink-0">✓</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>

          {/* CTA under the section */}
          <div className="text-center mt-12">
            <a
              href="https://app.spatchy.co/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]"
            >
              Start your 30-day free trial <ArrowRight size={18} />
            </a>
            <p className="text-slate-500 text-sm mt-3">No credit card required.</p>
          </div>

        </div>
      </section>

      {/* --- FEATURES GRID --- */}
      <FeaturesGrid />

      {/* --- WHITE-GLOVE MIGRATION SECTION --- */}
      <section className="py-24 px-6 bg-slate-900 relative overflow-hidden">
        {/* Subtle bg glow */}
        <div className="absolute top-0 right-0 w-[600px] h-[400px] bg-blue-600/10 rounded-full blur-[120px] -z-10" />

        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="text-center mb-16">
            <p className="text-blue-400 text-sm font-bold uppercase tracking-widest mb-4">
              We'll do the heavy lifting
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Switching software shouldn't suck. <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                So we do it for you.
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
              Coming from Jobber, Service Autopilot, or a stack of spreadsheets? Book a 60-minute call and we'll personally migrate your customers, jobs, schedules, and contacts into Spatchy. No double data entry. No "import wizard" headaches. No lost weekends.
            </p>
          </div>

          {/* Two-column grid: bullets on left, form on right */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">

            {/* LEFT: What's included */}
            <div className="space-y-6">

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={20} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Done in one call, by a real human</p>
                  <p className="text-slate-400 leading-relaxed">
                    Often me, the founder. No ticket queues, no chatbots, no "your migration specialist will reach out within 5–7 business days."
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={20} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Your team trained on what you'll actually use</p>
                  <p className="text-slate-400 leading-relaxed">
                    We'll walk through the features that matter for your business — not a generic 90-minute product tour you'll forget by Friday.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={20} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Direct line to support for your first 30 days</p>
                  <p className="text-slate-400 leading-relaxed">
                    Text, email, or call. You've got our number. We answer.
                  </p>
                </div>
              </div>

              <p className="text-slate-500 text-sm italic leading-relaxed pt-4 border-t border-white/10">
                Available to all new customers through 2026. We may need to scale this back as we grow — so come on in while it's still personal.
              </p>

            </div>

            {/* RIGHT: Form card */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">

              {migrationStatus === 'success' ? (
                <div className="flex items-start gap-3 bg-green-500/10 border border-green-500/30 rounded-xl p-5">
                  <CheckCircle2 size={24} className="text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-semibold mb-1">Got it — we'll be in touch!</p>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      We'll email you within one business day to schedule your migration call.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleMigrationSubmit} className="space-y-5">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1">Schedule my migration</h3>
                    <p className="text-slate-400 text-sm">
                      We'll be in touch within one business day to set up a time.
                    </p>
                  </div>

                  {/* Honeypot — visually hidden, must stay empty */}
                  <input
                    type="text"
                    name="botcheck"
                    value={migrationFormData.botcheck}
                    onChange={handleMigrationChange}
                    style={{ display: 'none' }}
                    tabIndex="-1"
                    autoComplete="off"
                  />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="migration-name" className="block text-sm font-medium text-slate-300 mb-2">
                        Your name
                      </label>
                      <input
                        type="text"
                        id="migration-name"
                        name="name"
                        value={migrationFormData.name}
                        onChange={handleMigrationChange}
                        required
                        disabled={migrationStatus === 'submitting'}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-colors disabled:opacity-50"
                        placeholder="Jane Smith"
                      />
                    </div>
                    <div>
                      <label htmlFor="migration-email" className="block text-sm font-medium text-slate-300 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="migration-email"
                        name="email"
                        value={migrationFormData.email}
                        onChange={handleMigrationChange}
                        required
                        disabled={migrationStatus === 'submitting'}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-colors disabled:opacity-50"
                        placeholder="jane@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="migration-phone" className="block text-sm font-medium text-slate-300 mb-2">
                      Phone number
                    </label>
                    <input
                      type="tel"
                      id="migration-phone"
                      name="phone"
                      value={migrationFormData.phone}
                      onChange={handleMigrationChange}
                      required
                      disabled={migrationStatus === 'submitting'}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-colors disabled:opacity-50"
                      placeholder="(807) 555-1234"
                    />
                  </div>

                  <div>
                    <label htmlFor="migration-from" className="block text-sm font-medium text-slate-300 mb-2">
                      What are you migrating from?
                    </label>
                    <select
                      id="migration-from"
                      name="migratingFrom"
                      value={migrationFormData.migratingFrom}
                      onChange={handleMigrationChange}
                      required
                      disabled={migrationStatus === 'submitting'}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-colors disabled:opacity-50"
                    >
                      <option value="" disabled className="bg-slate-800">Select one...</option>
                      <option value="Jobber" className="bg-slate-800">Jobber</option>
                      <option value="Service Autopilot" className="bg-slate-800">Service Autopilot</option>
                      <option value="Housecall Pro" className="bg-slate-800">Housecall Pro</option>
                      <option value="ServiceTitan" className="bg-slate-800">ServiceTitan</option>
                      <option value="FieldEdge" className="bg-slate-800">FieldEdge</option>
                      <option value="Spreadsheets / Excel" className="bg-slate-800">Spreadsheets / Excel</option>
                      <option value="Pen & paper" className="bg-slate-800">Pen & paper</option>
                      <option value="Other software" className="bg-slate-800">Other software</option>
                    </select>
                  </div>

                  {migrationStatus === 'error' && (
                    <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                      <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-red-200 text-sm">{migrationErrorMessage}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={migrationStatus === 'submitting'}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {migrationStatus === 'submitting' ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Schedule my migration <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}

            </div>

          </div>
        </div>
      </section>

      {/* --- MOBILE APP SECTION --- */}
      <section className="py-24 px-6 bg-slate-900 relative overflow-hidden">
        {/* Subtle bg glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-blue-600/10 rounded-full blur-[120px] -z-10" />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <p className="text-blue-400 text-sm font-bold uppercase tracking-widest mb-4">Field-ready</p>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Built for the truck, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">not just the desk.</span>
          </h2>
          <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed">
            Your crew updates job status, captures before/after photos, and gets routed to the next stop — straight from their phone. iOS and Android, included on every plan.
          </p>
          
          {/* App store badges */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href={IOS_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Spatchy on the App Store"
              className="inline-block transform hover:-translate-y-0.5 transition-transform"
            >
              <img 
                src={appStoreBadge} 
                alt="Download on the App Store" 
                className="h-14 w-auto"
              />
            </a>
            <a 
              href={ANDROID_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get Spatchy on Google Play"
              className="inline-block transform hover:-translate-y-0.5 transition-transform"
            >
              <img 
                src={googlePlayBadge} 
                alt="Get it on Google Play" 
                className="h-14 w-auto"
              />
            </a>
          </div>
        </div>
      </section>

      {/* --- FOOTER --- */}

    </div>
  );
};

export default LandingPage;